import { useMemo } from 'react';

import { MIN_LENGTH_TO_SEARCH } from 'common/constants';
import { AllStatuses, ItemOneOf } from 'interfaces';
import { SORT_TYPES, SortTypes } from './useSorting';
import { FiltersType } from './useFilters';
import { getSanitizedText } from '../helpers/validate-input';

const sorting = (a: ItemOneOf, b: ItemOneOf) =>
  new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();

export const useFilterList = <T extends ItemOneOf>(
  list: Array<T>,
  searchValue: string,
  sort: SortTypes,
  filters: FiltersType,
) => {
  const searchedList = useMemo(() => {
    if (searchValue.length < MIN_LENGTH_TO_SEARCH) return list;

    const sanitizedSearchValue = getSanitizedText(searchValue);

    return list.filter((item: T) =>
      getSanitizedText(item.title).includes(sanitizedSearchValue),
    );
  }, [list, searchValue]);

  const filteredList = useMemo(() => {
    const {
      filterDraft: draft,
      filterPendingApproval: pendingApproval,
      filterApproved: approved,
    } = filters;
    const noFilterApplied = !draft && !pendingApproval && !approved;
    const appliedFilters = [
      approved && AllStatuses.APPROVED,
      pendingApproval && AllStatuses.PENDING_APPROVAL,
      draft && AllStatuses.DRAFT,
    ];

    const filtered = searchedList.filter(
      (item: T) =>
        noFilterApplied ||
        appliedFilters.find((filter) => filter === item.status),
    );

    if (sort === SORT_TYPES.ASC) filtered.sort((a, b) => sorting(a, b));
    if (sort === SORT_TYPES.DESC) filtered.sort((a, b) => sorting(b, a));

    return filtered;
  }, [searchedList, sort, filters]);

  return { filteredList, searchedList };
};
